import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getOnboardingProgress } from "@/services/onboardingService";
import { saveTutorialStatus } from "@/services/commonServices";
import { saveTutorialStatusToLocal } from "@/helpers/common";

export const ONBOARDING_PROGRESS_KEY = ["onboardingProgress"];

export const onboardingStage = (progress) => {
  if (!progress) return "unknown";
  if (progress.dismissed) return "hidden";
  if (progress.isComplete) return "done";
  const posted = progress.steps.find((step) => step.id === "wantListing")?.done;
  return posted ? "settling" : "new";
};

export const QUIET_PROGRESS_OPTIONS = { staleTime: 60_000, refetchOnWindowFocus: false };

export const useOnboardingProgress = (enabled = true, options = {}) =>
  useQuery({
    queryKey: ONBOARDING_PROGRESS_KEY,
    queryFn: async () => {
      const response = await getOnboardingProgress();
      if (response?.errorCode !== 0) {
        throw new Error(response?.message || "Could not load your progress.");
      }
      return response.result;
    },
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    ...options,
  });

export const useMarkOnboardingMoment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (page) => {
      saveTutorialStatusToLocal(page);
      await saveTutorialStatus(page, 1);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_KEY }),
  });
};

export const useMarkMatchesSeen = (active, progress) => {
  const mark = useMarkOnboardingMoment();
  const sent = useRef(false);
  useEffect(() => {
    if (!active || !progress || sent.current) return;
    if (progress.steps.find((step) => step.id === "seeMatches")?.done) return;
    sent.current = true;
    mark.mutate(progress.matchesPage, {
      onError: () => {
        sent.current = false;
      },
    });
  }, [active, progress]);
};

export const useSetSidebarCollapsed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ page, collapsed }) => {
      // apiCall resolves with [] on a failed request instead of throwing, so
      // check the envelope or onError (and its rollback) would never run.
      const response = await saveTutorialStatus(page, collapsed ? 1 : 0);
      if (response?.errorCode !== 0) {
        throw new Error(response?.message || "Could not save the sidebar state.");
      }
    },
    onMutate: async ({ collapsed }) => {
      // A refetch already in flight would overwrite the optimistic value.
      await queryClient.cancelQueries({ queryKey: ONBOARDING_PROGRESS_KEY });
      const previous = queryClient.getQueryData(ONBOARDING_PROGRESS_KEY);
      if (previous) {
        queryClient.setQueryData(ONBOARDING_PROGRESS_KEY, {
          ...previous,
          sidebarCollapsed: collapsed,
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ONBOARDING_PROGRESS_KEY, context.previous);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ONBOARDING_PROGRESS_KEY }),
  });
};

export const useHideGettingStarted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tutorialPage) => {
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1);
    },
    onMutate: () => {
      const previous = queryClient.getQueryData(ONBOARDING_PROGRESS_KEY);
      if (previous) {
        queryClient.setQueryData(ONBOARDING_PROGRESS_KEY, {
          ...previous,
          dismissed: true,
        });
      }
      return { previous };
    },
    onError: (_error, _page, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ONBOARDING_PROGRESS_KEY, context.previous);
      }
    },
  });
};
